import {
  ChangeDetectionStrategy,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  inject,
  signal,
} from '@angular/core';
import { DomSanitizer, type SafeHtml } from '@angular/platform-browser';
import { TranslatePipe } from '@ngx-translate/core';
import { debugLog } from '../../../helpers/debugLog';
import type { QuizDraft } from '../../../models/quiz';
import { MetadataService } from '../../../services/metadata/metadata';
import { QuizService } from '../../../services/quiz/quiz';
import { ReaderApi } from '../../../services/reader-api/reader-api';

const URL_PATTERN = /(http|ftp|https):\/\/[\w-]+(\.[\w-]+)+([\w.,@?^=%&:/~+#-]*[\w@?^=%&/~+#-])?/;
const IMAGE_EXTENSION_PATTERN = /\.(jpeg|jpg|gif|png)$/i;

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

@Component({
  selector: 'nr-annotation-dialog',
  imports: [TranslatePipe],
  templateUrl: './annotation-dialog.html',
  styleUrl: './annotation-dialog.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AnnotationDialogComponent {
  private readonly quizService = inject(QuizService);
  private readonly metadataService = inject(MetadataService);
  private readonly readerApi = inject(ReaderApi);
  private readonly sanitizer = inject(DomSanitizer);

  private lastEmbeddedUrl?: string;

  readonly draft = this.quizService.quizDraft;

  readonly canManageQuestions = this.metadataService.canManageQuestions;

  readonly confirmDelete = signal(false);

  readonly saving = signal(false);

  readonly embedHtml = signal<SafeHtml | undefined>(undefined);

  readonly text = computed(() => this.draft()?.questions[0]?.text ?? '');

  readonly isValid = computed(() => this.text().trim().length > 0);

  readonly embedImageUrl = computed(() => {
    if (this.draft()?.embebbedContentType !== 'image') return undefined;
    return URL_PATTERN.exec(this.text())?.[0];
  });

  readonly linkedText = computed<SafeHtml>(() => {
    const match = URL_PATTERN.exec(this.text());
    const escaped = escapeHtml(this.text());
    if (!match) return this.sanitizer.bypassSecurityTrustHtml(escaped);
    const [url] = match;
    const linked = escapeHtml(this.text()).replace(
      escapeHtml(url),
      `<a href="${escapeHtml(url)}" target="_blank" rel="noopener">${escapeHtml(url)}</a>`,
    );
    return this.sanitizer.bypassSecurityTrustHtml(linked);
  });

  constructor() {
    const url = URL_PATTERN.exec(this.text())?.[0];
    if (url && this.draft()?.embebbedContentType && this.draft()?.embebbedContentType !== 'image') {
      this.lastEmbeddedUrl = url;
      void this.loadEmbed(url);
    }
  }

  updateText(value: string): void {
    this.updateDraft((draft) => ({
      ...draft,
      questions: [{ ...draft.questions[0], text: value }],
    }));
    this.detectEmbed(value);
  }

  async save(): Promise<void> {
    if (!this.isValid() || this.saving()) return;
    this.saving.set(true);
    try {
      await this.quizService.saveQuizDraft();
    } finally {
      this.saving.set(false);
    }
  }

  cancel(): void {
    if (this.saving()) return;
    this.quizService.cancelQuizDraft();
  }

  async confirmDeleteAnnotation(): Promise<void> {
    const draft = this.draft();
    this.confirmDelete.set(false);
    if (!draft?.id) return;
    await this.quizService.deleteQuiz(draft.id, draft.relId);
  }

  private detectEmbed(text: string): void {
    const url = URL_PATTERN.exec(text)?.[0];
    if (!url) {
      this.lastEmbeddedUrl = undefined;
      this.embedHtml.set(undefined);
      this.updateDraft((draft) => ({ ...draft, embebbedContentType: undefined }));
      return;
    }
    if (IMAGE_EXTENSION_PATTERN.test(url)) {
      this.lastEmbeddedUrl = url;
      this.embedHtml.set(undefined);
      this.updateDraft((draft) => ({ ...draft, embebbedContentType: 'image' }));
      return;
    }
    if (url === this.lastEmbeddedUrl) return;
    this.lastEmbeddedUrl = url;
    void this.loadEmbed(url);
  }

  private async loadEmbed(url: string): Promise<void> {
    try {
      const result = await this.readerApi.getEmbeddedContent(url);
      if (this.lastEmbeddedUrl !== url) return;
      if (!result?.html) {
        this.embedHtml.set(undefined);
        this.updateDraft((draft) => ({ ...draft, embebbedContentType: undefined }));
        return;
      }
      this.embedHtml.set(this.sanitizer.bypassSecurityTrustHtml(result.html));
      this.updateDraft((draft) => ({ ...draft, embebbedContentType: result.type }));
    } catch (error) {
      debugLog('Error loading embedded content:', error);
      this.embedHtml.set(undefined);
    }
  }

  private updateDraft(updater: (draft: QuizDraft) => QuizDraft): void {
    const current = this.draft();
    if (!current) return;
    this.quizService.quizDraft.set(updater(current));
  }
}
